import React from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { routes } from '../../routes'

import {
  Text,
  Link,
  Navbar,
  useResponsiveDropdown,
  Dropdown,
  useBreakpoint,
} from '@sketch/components'

import {
  Header,
  LogoWrapper,
  Logo,
  MarketingWebsiteBackToWorkspaceButton,
  StyledAvatar,
  HelpButton,
  HelpChevron,
  HelpDropdownAnchor,
  HelpExternalArrow,
  BackButton,
  LeftArrowStyled as LeftArrow,
  StyledLinkButton,
  SquareAvatar,
} from './IndexLayout.styles'

import { SKETCH_WEBSITE } from '@sketch/env-config'
import { useUserProfile } from '../../libs'
import { useScrollTop } from '@sketch/utils'

const headerLinkProps = {
  'sign-up': {
    link: routes.SIGN_UP.create({}),
    text: 'New to Sketch?',
    buttonCopy: 'Create an Account',
    variant: 'secondary',
  },
  'sign-in': {
    link: routes.SIGN_IN.create({}),
    text: 'Already have an account?',
    buttonCopy: 'Sign In',
    variant: 'secondary',
  },
  'sign-in-reset': {
    link: routes.SIGN_IN.create({}),
    text: 'Remembered your password?',
    buttonCopy: 'Sign In',
    variant: 'secondary',
  },
  entry: {
    link: routes.ENTRY.create({}),
    text: null,
    buttonCopy: 'Go Back',
    variant: 'secondary',
  },
  'back-sign-in': {
    link: routes.SIGN_IN.create({}),
    text: null,
    buttonCopy: 'Back to Sign In',
    variant: 'secondary',
  },
  'check-inbox': {
    link: routes.SIGN_OUT.create({}),
    text: null,
    buttonCopy: 'Sign Out',
    variant: 'tertiary',
  },
  'choose-your-path': {
    link: routes.WORKSPACE_AGNOSTIC_SHARES.create({}),
    linkWorkspace: (workspaceId: string) =>
      routes.WORKSPACE_SHARES.create({ workspaceId }),
    text: null,
    buttonCopy: 'My Workspace',
    variant: 'tertiary',
  },
  'link-sso-account': {
    link: routes.WORKSPACE_AGNOSTIC_SHARES.create({}),
    linkWorkspace: (workspaceId: string) =>
      routes.WORKSPACE_SHARES.create({ workspaceId }),
    text: null,
    buttonCopy: 'My Workspace',
    variant: 'tertiary',
  },
  'workspace-settings': {
    link: routes.WORKSPACE_AGNOSTIC_SETTINGS_PEOPLE.create({}),
    linkWorkspace: (workspaceId: string) =>
      routes.WORKSPACE_SETTINGS_PEOPLE.create({ workspaceId }),
    text: null,
    buttonCopy: 'Back to Workspace',
    variant: 'secondary',
  },
  'workspace-shares': {
    link: routes.WORKSPACE_AGNOSTIC_SHARES.create({}),
    linkWorkspace: (workspaceId: string) =>
      routes.WORKSPACE_SHARES.create({ workspaceId }),
    text: null,
    buttonCopy: 'Back to Workspace',
    variant: 'secondary',
  },
  'no-workspace-access': {
    link: routes.ENTRY.create({}),
    text: null,
    buttonCopy: 'Back to Workspace',
    variant: 'secondary',
  },
} as const

const ignoreMatchPath = (
  returnMatch: ReturnType<typeof matchPath>,
  pathToIgnore: string
) => {
  if (returnMatch && matchPath(returnMatch.url, { path: pathToIgnore })) {
    return null
  }

  return returnMatch
}

type HeaderLinkType = typeof headerLinkProps
type HeaderLinkKeys = keyof HeaderLinkType | 'none'
type HeaderLinkItem = HeaderLinkType[keyof HeaderLinkType]

interface HeaderLinkProps {
  headerLink?: HeaderLinkKeys
  ignoreWorkspaceMatch?: boolean
}

const HelpDropdown = () => (
  <>
    <HelpDropdownAnchor href={`${SKETCH_WEBSITE}/docs/`}>
      Documentation
    </HelpDropdownAnchor>
    <HelpDropdownAnchor href={`${SKETCH_WEBSITE}/support/`}>
      Support
    </HelpDropdownAnchor>
    <HelpDropdownAnchor href={`${SKETCH_WEBSITE}/support/contact/`}>
      Contact Us
    </HelpDropdownAnchor>
    <Dropdown.Divider />
    <HelpDropdownAnchor href="https://forum.sketch.com" target="_blank">
      Community Forum <HelpExternalArrow />
    </HelpDropdownAnchor>
    <HelpDropdownAnchor href="https://status.sketch.com" target="_blank">
      Service Status <HelpExternalArrow />
    </HelpDropdownAnchor>
  </>
)

export const HeaderLink: React.FCC<HeaderLinkProps> = ({
  headerLink = 'none',
  ignoreWorkspaceMatch,
  children,
}) => {
  const { state, search, pathname } = useLocation()
  const { data } = useUserProfile(true)
  const isTabletOrBigger = useBreakpoint('sm')

  const match = ignoreMatchPath(
    matchPath<{ workspaceId: string }>(pathname, {
      path: routes.WORKSPACE.template(),
    }),
    routes.WORKSPACE_CREATE.template()
  )

  const { workspaceId } = match?.params || {}
  const hasBackToWorkspaceButton =
    headerLink === 'workspace-settings' ||
    headerLink === 'workspace-shares' ||
    headerLink === 'link-sso-account' ||
    headerLink === 'no-workspace-access'

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: HelpDropdown,
    dropdownStyle: { minWidth: 270 },
  })

  const buildLink = (item: HeaderLinkItem) => {
    if (workspaceId && 'linkWorkspace' in item && !ignoreWorkspaceMatch) {
      return item.linkWorkspace(workspaceId)
    }

    return item.link
  }

  let headerLinkButton = null
  const isCheckInboxLink = headerLink === 'check-inbox'
  const isChooseYourPathLink = headerLink === 'choose-your-path'

  if (hasBackToWorkspaceButton) {
    headerLinkButton = isTabletOrBigger ? (
      <MarketingWebsiteBackToWorkspaceButton
        variant="secondary"
        size="40"
        to={{
          pathname: buildLink(headerLinkProps[headerLink]),
          state,
        }}
      >
        <StyledAvatar
          src={data?.me.avatar?.small}
          size="24px"
          name={data?.me.name || data?.me.email || '?'}
        />
        {headerLinkProps[headerLink].buttonCopy}
      </MarketingWebsiteBackToWorkspaceButton>
    ) : (
      <Link to={{ pathname: buildLink(headerLinkProps[headerLink]), state }}>
        <SquareAvatar
          src={data?.me.avatar?.small}
          size="32px"
          name={data?.me.name || data?.me.email || '?'}
        />
      </Link>
    )
  } else if (headerLink !== 'none') {
    if (headerLink === 'entry') {
      headerLinkButton = (
        <BackButton
          to={{
            pathname: buildLink(headerLinkProps[headerLink]),
            state,
            search,
          }}
        >
          <LeftArrow /> {headerLinkProps[headerLink].buttonCopy}
        </BackButton>
      )
    } else {
      headerLinkButton = (
        <StyledLinkButton
          $noBackground={isCheckInboxLink || isChooseYourPathLink}
          variant={headerLinkProps[headerLink].variant}
          to={{
            pathname: buildLink(headerLinkProps[headerLink]),
            state,
            search,
          }}
        >
          {headerLinkProps[headerLink].buttonCopy}
        </StyledLinkButton>
      )
    }
  }

  return (
    <Navbar.Section align="end" ml={2}>
      {content}

      <HelpButton
        size="48"
        icon={HelpChevron}
        iconPosition="right"
        {...buttonProps}
      >
        Help
      </HelpButton>

      {children}

      {headerLink !== 'none' && headerLinkProps[headerLink].text && (
        <Text textStyle="copy.secondary.standard.D" mr={4}>
          {headerLinkProps[headerLink].text}
        </Text>
      )}

      {headerLinkButton}
    </Navbar.Section>
  )
}

export interface IndexLayoutHeaderProps {
  headerLink?: HeaderLinkKeys
  disableLogoLink?: boolean
  ignoreWorkspaceMatch?: boolean
}

export const IndexLayoutHeader: React.FCC<IndexLayoutHeaderProps> = ({
  headerLink,
  disableLogoLink,
  ignoreWorkspaceMatch,
}) => {
  const { isScrollTop } = useScrollTop()

  return (
    <Header data-scrolltop={isScrollTop}>
      <LogoWrapper data-testid="sketch-logo">
        {disableLogoLink ? (
          <Logo />
        ) : (
          <Link to={routes.ENTRY.create({})}>
            <Logo />
          </Link>
        )}
      </LogoWrapper>
      <HeaderLink
        headerLink={headerLink}
        ignoreWorkspaceMatch={ignoreWorkspaceMatch}
      />
    </Header>
  )
}
